import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { useDepositCacheInvalidator } from '@/hooks/useContractDeposits';
import { REFUND_EPSILON, remainingRefundable } from '@/lib/depositStatus';
import { formatBusinessDate, formatCurrency } from '@/lib/format';
import DepositAgreementBlock from '@/components/contracts/deposits/DepositAgreementBlock';
import type { DepositRow } from '@/components/contracts/deposits/types';

interface EditDepositSheetProps {
    /** Row being edited; null closes the sheet. */
    deposit: DepositRow | null;
    onOpenChange: (open: boolean) => void;
    /** Row menu → Delete opens the sheet with the confirmation already up. */
    startInDeleteConfirm?: boolean;
    /** Hands off to RefundDepositSheet (the parent owns both sheets). */
    onManageRefunds?: () => void;
}

/**
 * Edit / delete one deposit: re-point it at a different CREDIT transaction,
 * amend the depositor remarks, or remove the link record entirely.
 *
 * Refund-aware: a deposit that has already been partly refunded cannot be
 * re-pointed at a transaction smaller than what has gone back out — that would
 * break the Σ refunds ≤ deposit invariant (the DB trigger would reject it too,
 * with a less friendly message).
 */
export default function EditDepositSheet({
    deposit,
    onOpenChange,
    startInDeleteConfirm = false,
    onManageRefunds,
}: EditDepositSheetProps) {
    const invalidate = useDepositCacheInvalidator();

    const [transactionId, setTransactionId] = useState<number | null>(null);
    const [transactionAmount, setTransactionAmount] = useState(0);
    const [remarks, setRemarks] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Primitive deps (not the row object) so a background refetch that changes
    // nothing cannot stomp on what is being typed.
    const depositId = deposit?.id ?? null;
    const depositTransactionId = deposit?.transaction_id ?? null;
    const depositAmount = deposit?.amount ?? 0;
    const depositRemarks = deposit?.remarks ?? '';

    useEffect(() => {
        if (depositId == null) return;
        setTransactionId(depositTransactionId);
        setTransactionAmount(depositAmount);
        setRemarks(depositRemarks);
        setShowDeleteConfirm(startInDeleteConfirm);
    }, [depositId, depositTransactionId, depositAmount, depositRemarks, startInDeleteConfirm]);

    const refundedTotal = deposit?.refunded_total ?? 0;
    const refundCount = deposit?.refund_count ?? 0;
    const currency = deposit?.currency ?? 'GHS';
    const remaining = remainingRefundable(depositAmount, refundedTotal);

    /** Blocks a swap to a transaction that has already been over-refunded. */
    const smallerTransactionError = useMemo(() => {
        if (!deposit || refundCount === 0) return null;
        if (transactionId == null || transactionId === deposit.transaction_id) return null;
        if (transactionAmount >= refundedTotal - REFUND_EPSILON) return null;
        return `Cannot switch to a smaller transaction — ${formatCurrency(refundedTotal, currency)} has already been refunded.`;
    }, [deposit, refundCount, transactionId, transactionAmount, refundedTotal, currency]);

    const updateMutation = useMutation({
        mutationFn: async () => {
            if (!deposit || !transactionId) throw new Error('Missing data');
            const { error } = await supabase
                .from('contract_deposits')
                .update({
                    transaction_id: transactionId,
                    remarks: remarks.trim() || null,
                })
                .eq('id', deposit.id);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Deposit updated');
            invalidate({ depositId: deposit?.id, contractId: deposit?.contract_id });
            onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!deposit) throw new Error('No deposit selected');
            const { error } = await supabase
                .from('contract_deposits')
                .delete()
                .eq('id', deposit.id);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Deposit deleted');
            invalidate({ depositId: deposit?.id, contractId: deposit?.contract_id });
            onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
    });

    const driverName = deposit
        ? [deposit.driver_first_name, deposit.driver_middle_name, deposit.driver_last_name]
              .filter(Boolean)
              .join(' ')
        : '';
    const summary = deposit
        ? [
              driverName || deposit.remarks || 'Unassigned deposit',
              formatCurrency(deposit.amount, deposit.currency),
              formatBusinessDate(deposit.transaction_at ?? deposit.created_at),
          ].join(' · ')
        : '';

    return (
        <>
            <Sheet open={!!deposit} onOpenChange={(open) => { if (!open) onOpenChange(false); }}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !w-[40vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
                >
                    <SheetHeader>
                        <SheetTitle>Edit Deposit</SheetTitle>
                        <SheetDescription>{summary}</SheetDescription>
                    </SheetHeader>

                    <div className="flex-1 mt-6 space-y-6 overflow-y-auto pr-1">
                        {/* Refund notice — this deposit is no longer fully held */}
                        {refundCount > 0 && (
                            <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 flex items-start justify-between gap-3">
                                <p className="text-xs text-amber-800 dark:text-amber-300">
                                    {refundCount} refund{refundCount === 1 ? '' : 's'} totalling{' '}
                                    {formatCurrency(refundedTotal, currency)}. Remaining held:{' '}
                                    {formatCurrency(remaining, currency)}.
                                </p>
                                {onManageRefunds && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 shrink-0 text-amber-800 hover:text-amber-900 hover:bg-amber-100 dark:text-amber-300 dark:hover:text-amber-200 dark:hover:bg-amber-900/40"
                                        onClick={onManageRefunds}
                                    >
                                        <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                                        Manage refunds
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Transaction */}
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Bank Transaction</Label>
                            <TransactionSelector
                                value={transactionId}
                                onChange={(id, transaction) => {
                                    setTransactionId(id);
                                    setTransactionAmount(transaction?.amount ?? 0);
                                }}
                                placeholder="Select a transaction..."
                                defaultType="CREDIT"
                            />
                            {smallerTransactionError && (
                                <p className="text-xs text-destructive">{smallerTransactionError}</p>
                            )}
                        </div>

                        {/* Remarks */}
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Remarks</Label>
                            <Textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Driver name, phone number, address, or any identifying information..."
                                rows={3}
                            />
                        </div>

                        {/* The deposit's own agreement. It lives here rather than on the contract
                            setup board because the deposits this feature exists for have no
                            contract yet — the depositor signs through a link, with no account. */}
                        {/* KEYED ON THE DEPOSIT. The block holds session-only state — a raw
                            token that was minted once, a finalize attempt in flight — and none
                            of it is true of a different deposit. */}
                        {deposit && <DepositAgreementBlock key={deposit.id} deposit={deposit} />}
                    </div>

                    <div className="pt-4 border-t flex items-center justify-between">
                        <Button
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={deleteMutation.isPending}
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button
                                onClick={() => updateMutation.mutate()}
                                disabled={!transactionId || !!smallerTransactionError || updateMutation.isPending}
                            >
                                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Save
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Deposit Record?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove this deposit record. The underlying bank transaction will not be affected and can be reassigned to a new deposit later.
                            {refundCount > 0 && (
                                <>
                                    {' '}This deposit has {refundCount} refund record{refundCount === 1 ? '' : 's'} which will also be removed.{' '}
                                    {refundCount === 1
                                        ? 'The refund DEBIT transaction itself is not deleted and returns to the unassigned pool.'
                                        : 'The refund DEBIT transactions themselves are not deleted and return to the unassigned pool.'}
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteMutation.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
